"use client"

import * as React from "react"
import { cn, countCharacters } from "@/lib/utils"

export interface TextareaWithMaxLengthProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  containerClassName?: string;
  Extensions?: React.FunctionComponent;
}

export const TextareaWithMaxLength = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, TextareaWithMaxLengthProps>(
  ({ className, containerClassName = '', Extensions, rows, ...props }, ref) => {
    const { maxLength, ...restProps } = props;
    const isSingleLine = rows === 1;
    // 为 input 准备 props，排除 rows
    const inputProps = isSingleLine ? (() => {
      const { rows: _, ...inputRest } = restProps as typeof restProps & { rows?: number };
      return inputRest;
    })() : restProps;
    let charCount = 0;
    if (maxLength) {
      const { chinese = 0, english = 0 } = countCharacters(props.value?.toString() || '');
      charCount = chinese + english;
    }

    const handleKeyDownInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (maxLength && charCount >= maxLength) {
        // 允许删除、退格、方向键等控制键
        const allowedKeys = [
          'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
          'Home', 'End', 'PageUp', 'PageDown', 'Tab', 'Enter'
        ];

        // 允许 Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X 等组合键
        if (e.ctrlKey || e.metaKey) {
          return;
        }

        // 允许删除操作
        if (allowedKeys.includes(e.key)) {
          return;
        }

        // 允许空格输入（空格不计入字符数）
        if (e.key === ' ') {
          return;
        }

        // 阻止 Mac 系统的自动替换（如双空格转句号），但只在达到限制时阻止
        if (e.key === '.' && charCount >= maxLength) {
          e.preventDefault();
          return;
        }

        // 对于其他字符，检查是否会导致超过限制
        // 这里我们允许输入，但在 onInput 中处理
      }

      // 调用原有的 onKeyDown 处理器
      if (props.onKeyDown) {
        props.onKeyDown(e as unknown as React.KeyboardEvent<HTMLTextAreaElement>);
      }
    };

    const handleKeyDownTextarea = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (maxLength && charCount >= maxLength) {
        // 允许删除、退格、方向键等控制键
        const allowedKeys = [
          'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
          'Home', 'End', 'PageUp', 'PageDown', 'Tab', 'Enter'
        ];

        // 允许 Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X 等组合键
        if (e.ctrlKey || e.metaKey) {
          return;
        }

        // 允许删除操作
        if (allowedKeys.includes(e.key)) {
          return;
        }

        // 允许空格输入（空格不计入字符数）
        if (e.key === ' ') {
          return;
        }

        // 阻止 Mac 系统的自动替换（如双空格转句号），但只在达到限制时阻止
        if (e.key === '.' && charCount >= maxLength) {
          e.preventDefault();
          return;
        }

        // 对于其他字符，检查是否会导致超过限制
        // 这里我们允许输入，但在 onInput 中处理
      }

      // 调用原有的 onKeyDown 处理器
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };

    const handleInputInput = (e: React.FormEvent<HTMLInputElement>) => {
      if (maxLength) {
        const target = e.target as HTMLInputElement;
        const { chinese = 0, english = 0 } = countCharacters(target.value || '');
        const newCharCount = chinese + english;

        // 如果超过限制，恢复到之前的值
        if (newCharCount > maxLength) {
          // 阻止输入并恢复原值
          target.value = props.value?.toString() || '';
          return;
        }
      }

      // 调用原有的 onInput 处理器
      if (props.onInput) {
        props.onInput(e as unknown as React.FormEvent<HTMLTextAreaElement>);
      }
    };

    const handleInputTextarea = (e: React.FormEvent<HTMLTextAreaElement>) => {
      if (maxLength) {
        const target = e.target as HTMLTextAreaElement;
        const { chinese = 0, english = 0 } = countCharacters(target.value || '');
        const newCharCount = chinese + english;

        // 如果超过限制，恢复到之前的值
        if (newCharCount > maxLength) {
          // 阻止输入并恢复原值
          target.value = props.value?.toString() || '';
          return;
        }
      }

      // 调用原有的 onInput 处理器
      if (props.onInput) {
        props.onInput(e);
      }
    };

    const handleBeforeInputInput = (e: React.FormEvent<HTMLInputElement>) => {
      if (maxLength && charCount >= maxLength) {
        // 检查输入内容，如果是空格则允许（空格不计入字符数）
        const inputEvent = e.nativeEvent as InputEvent;
        if (inputEvent.data === ' ') {
          return; // 允许空格输入
        }
        // 阻止系统自动替换的句号
        if (inputEvent.data === '.') {
          e.preventDefault();
          return;
        }
      }
    };

    const handleBeforeInputTextarea = (e: React.FormEvent<HTMLTextAreaElement>) => {
      if (maxLength && charCount >= maxLength) {
        // 检查输入内容，如果是空格则允许（空格不计入字符数）
        const inputEvent = e.nativeEvent as InputEvent;
        if (inputEvent.data === ' ') {
          return; // 允许空格输入
        }
        // 阻止系统自动替换的句号
        if (inputEvent.data === '.') {
          e.preventDefault();
          return;
        }
      }
    };

    return (
      <div className={
        cn(
          "relative w-full flex-1 rounded-lg bg-muted/50 h-fit flex flex-col",
          containerClassName,
        )
      }>
        {isSingleLine ? (
          <input
            type="text"
            className={cn(
              "flex h-full w-full rounded-lg px-3 py-2.5 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 bg-transparent placeholder-annotation-foreground",
              maxLength && "pr-12",
              className
            )}
            ref={ref as React.Ref<HTMLInputElement>}
            onKeyDown={handleKeyDownInput}
            onInput={handleInputInput}
            onBeforeInput={handleBeforeInputInput}
            {...(inputProps as React.InputHTMLAttributes<HTMLInputElement>)}
          />
        ) : (
          <textarea
            className={cn(
              "flex h-full w-full rounded-lg p-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-none bg-transparent placeholder-annotation-foreground",
              className
            )}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            rows={rows}
            onKeyDown={handleKeyDownTextarea}
            onInput={handleInputTextarea}
            onBeforeInput={handleBeforeInputTextarea}
            {...restProps}
          ></textarea>
        )}
        {
          Extensions ? (<Extensions {...props}></Extensions>) : null
        }
        {
          (maxLength) ? (
            <div
              className={
                cn(
                  "absolute bottom-3 right-3 text-annotation-foreground text-xs pointer-events-none",
                  charCount >= maxLength && "text-destructive"
                )
              }
            >
              {maxLength ? `${charCount} / ${maxLength}` : null}
            </div>
          ) : null
        }
      </div>
    )
  }
)

TextareaWithMaxLength.displayName = "TextareaWithMaxLength"
